import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Wc from '@mui/icons-material/Wc';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import GroupsIcon from '@mui/icons-material/Groups';
import RuleScreen from '../../shared/RuleScreen';
import SundargarhFilterBar from '../../shared/SundargarhFilterBar';
import StatTile from '../../shared/StatTile';
import DataTable from '../../shared/DataTable';
import Pagination, { normalizePagination } from '../../shared/Pagination';
import DetailDrawer from '../../shared/DetailDrawer';
import { buildParams } from '../../shared/apiParams';

const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const STATUS_LABEL = { correct: 'Correct', mismatch: 'Gender Mismatch' };

const pctCell = (b) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: 'var(--muted)', marginLeft: 4 }}>({b?.pct || 0}%)</span>
  </>
);

export default function SundargarhGenderRoster({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, status: 'all', records: [], loading: false });
  const [membersPopup, setMembersPopup] = useState({ open: false, record: null, members: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/rules/gender-roster/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh gender-roster data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalSurveys = summary?.totalSurveys ?? rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);
  const correct = summary?.correct?.count ?? 0;
  const mismatch = summary?.mismatch?.count ?? 0;

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile label="Correct" value={correct} icon={CheckCircleIcon} accent="var(--success)" sub={`${summary?.correct?.pct ?? 0}% of total`} />
      <StatTile label="Gender Mismatch" value={mismatch} icon={ErrorIcon} accent="var(--danger)" sub={`${summary?.mismatch?.pct ?? 0}% of total`} />
    </>
  );

  const openDrilldown = async (row, status = 'all') => {
    setDrawer({ open: true, row, status, records: [], loading: true });
    try {
      const params = buildParams(applied, { extra: { groupBy: activeTab, groupValue: row.name, status, limit: 100 } });
      const res = await axios.get(`${API_URL}/sundargarh/rules/gender-roster/details?${params}`);
      setDrawer({ open: true, row, status, records: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching gender-roster detail records:', err);
      setDrawer({ open: true, row, status, records: [], loading: false });
    }
  };

  const statusCell = (row, key) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, key); }}>
      {pctCell(row[key])}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'correct', label: 'Correct', align: 'center', render: (r) => statusCell(r, 'correct') },
    { key: 'mismatch', label: 'Mismatch (Gender)', align: 'center', render: (r) => statusCell(r, 'mismatch') },
  ];

  const openMembersPopup = async (record) => {
    setMembersPopup({ open: true, record, members: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}/sundargarh/rules/gender-roster/members?uuid=${encodeURIComponent(record.uuid)}`);
      setMembersPopup({ open: true, record, members: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching household members:', err);
      setMembersPopup({ open: true, record, members: [], loading: false });
    }
  };

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    {
      key: 'household_id', label: 'Household ID',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openMembersPopup(r); }}>
          {r.household_id || '—'}
        </span>
      ),
    },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'memberCount', label: 'Members', align: 'center', render: (r) => r.memberCount ?? '—' },
    { key: 'mismatchCount', label: 'Mismatched', align: 'center', render: (r) => <span className={`badge ${r.mismatchCount > 0 ? 'badge-danger' : 'badge-success'}`}>{r.mismatchCount ?? 0}</span> },
    { key: 'ulb', label: 'ULB', render: (r) => r.ulb || '—' },
    { key: 'ward', label: 'Ward', render: (r) => r.ward || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
  ];

  return (
    <RuleScreen
      title={rule?.title || 'Respondent Gender vs Roster'}
      subtitle="Flags households where a member's recorded gender doesn't match what their stated relation (Son, Daughter, Wife, Husband, etc.) implies."
      icon={Wc}
      backTo={rule?.category ? `/sundargarh-category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button key={t.key} className={`tab-btn${activeTab === t.key ? ' active' : ''}`} onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}>
                {t.label}
              </button>
            ))}
          </div>
          <SundargarhFilterBar onApply={(v) => { setApplied(v); setPage(1); }} onClear={() => { setApplied(EMPTY); setPage(1); }} loading={loading} />
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Gender vs Relation by {tab.label.replace('By ', '')}</h3>
        <Pagination pagination={pagination} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} unitLabel="groups" shown={rows.length} />
        <DataTable columns={columns} rows={rows} loading={loading} emptyMessage='No data found. Adjust filters and click "Apply Filters".' onRowClick={(r) => openDrilldown(r, 'all')} getRowKey={(r, i) => `${r.name}-${i}`} />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${STATUS_LABEL[drawer.status] || 'All Surveys'}` : ''}
        sections={
          drawer.row
            ? [{ title: 'Breakdown', items: [
                { label: 'Total Surveys', value: drawer.row.totalSurveys || 0 },
                { label: 'Correct', value: drawer.row.correct?.count || 0 },
                { label: 'Gender Mismatch', value: drawer.row.mismatch?.count || 0 },
              ] }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '-0.4rem' }}>Click a household ID to see each member's relation and gender.</p>
          <DataTable columns={drawerRecordColumns} rows={drawer.records} loading={drawer.loading} emptyMessage="No households found." getRowKey={(r, i) => r.uuid || i} />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={membersPopup.open}
        onClose={() => setMembersPopup((p) => ({ ...p, open: false }))}
        title={membersPopup.record ? `Members: ${membersPopup.record.household_id}` : ''}
        sections={membersPopup.record ? [{ title: 'Household', items: [
          { label: 'Respondent', value: membersPopup.record.respondent_name },
          { label: 'Mismatched Members', value: membersPopup.record.mismatchCount },
        ] }] : []}
      >
        <div className="detail-section">
          <h4>Members ({membersPopup.members.length})</h4>
          <DataTable
            columns={[
              { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
              { key: 'name', label: 'Name', render: (m) => <span className={m.isMismatch ? 'cell-strong' : ''} style={m.isMismatch ? { color: 'var(--danger)' } : undefined}>{m.name || '—'}</span> },
              { key: 'relation', label: 'Relation', render: (m) => m.relation || '—' },
              { key: 'gender', label: 'Gender', render: (m) => m.gender || '—' },
              { key: 'expectedGender', label: 'Expected Gender', render: (m) => m.expectedGender || '—' },
              { key: 'age', label: 'Age', align: 'center', render: (m) => m.age ?? '—' },
              { key: 'isMismatch', label: 'Status', align: 'center', render: (m) => <span className={`badge ${m.isMismatch ? 'badge-danger' : 'badge-success'}`}>{m.isMismatch ? 'Mismatch' : 'OK'}</span> },
            ]}
            rows={membersPopup.members}
            loading={membersPopup.loading}
            emptyMessage="No members found for this household."
            getRowKey={(m, i) => `${m.name}-${i}`}
          />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
